import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Users, Package, Check, Clock, RefreshCw, Trash2, ArrowRight, List, Printer, Truck, Search } from 'lucide-react';
import WarehouseRejectModal from './WarehouseRejectModal';
import OrderDetailDrawer from '../shared/OrderDetailDrawer';
import ConfirmDialog from '../shared/ConfirmDialog';
import { executeTransition, getWorkflowByPlan, searchOrders, batchConfirmProduct, downloadPickingListPdf } from '../../../../services/api/salesOrderApi';
import { getCurrentTenant } from '../../../../services/api/companyApi';

/**
 * Highly simplified Card matching the user's mockup requirements:
 * "mainly the order id, customer name and list of products"
 * with Revise / Approve / Reject buttons.
 */
const SimpleWarehouseOrderCard = ({ order, transitions = [], onTransition, onRevise, loadingTransition, onViewDetail }) => {
    // Helper to get action buttons
    const getActionStyle = (name) => {
        name = (name || '').toLowerCase();
        if (name.includes('approve')) return 'bg-teal-500 text-white hover:bg-teal-600 shadow-md shadow-teal-200/50';
        if (name.includes('reject') || name.includes('return')) return 'bg-rose-500 text-white hover:bg-rose-600 shadow-md shadow-rose-200/50';
        if (name.includes('revise')) return 'bg-violet-500 text-white hover:bg-violet-600 shadow-md shadow-violet-200/50';
        return 'bg-blue-500 text-white hover:bg-blue-600 shadow-sm';
    };

    const getTransitionIcon = (name) => {
        name = (name || '').toLowerCase();
        if (name.includes('approve')) return <Check size={14} strokeWidth={3} />;
        if (name.includes('reject')) return <XIcon size={14} strokeWidth={3} />;
        if (name.includes('revise')) return <EditIcon size={14} strokeWidth={2.5} />;
        if (name.includes('deliver') || name.includes('dispatch')) return <Clock size={14} />;
        return <ArrowRight size={14} />;
    };

    const customerName = order.customer?.name || 'Unknown Customer';
    const orderRef = order.orderRef || `#${order.id?.slice(-8)}`;
    const vehicleNo = order.assignedSalesPersonVehicleNumber || order.delegatedSalesPersonVehicleNumber;

    return (
        <div className="bg-[#f2ebfa] rounded-2xl p-4 flex flex-col shadow-sm border border-[#e6d5ff] transition-all relative">
            {/* Header */}
            <div className="flex justify-between items-start mb-3 cursor-pointer" onClick={() => onViewDetail && onViewDetail(order)}>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-400 to-purple-500 text-white flex items-center justify-center font-bold text-lg shadow-sm">
                        {customerName[0]}
                    </div>
                    <div>
                        <h3 className="font-bold text-gray-900 text-base leading-tight">{customerName}</h3>
                        <div className="flex items-center gap-2 mt-0.5">
                            <p className="text-xs text-gray-500 font-medium">{orderRef}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-[#e6d0ff] text-violet-800 text-[10px] font-bold px-2.5 py-1 rounded-md uppercase tracking-wide">
                    {order.status === 'NEW_ORDER' ? 'New Order' : order.status?.replace(/_/g, ' ')}
                </div>
            </div>

            <div className="flex-1 mb-4 mt-2">
                <p className="text-[11px] text-gray-500 mb-2 font-medium">Items Sent to Warehouse:</p>
                <div className="space-y-1">
                    {(order.items || []).map((item, idx) => {
                        const isPicked = item.warehouseQuantity !== null && 
                                       item.warehouseQuantity !== undefined && 
                                       Number(item.warehouseQuantity) >= Number(item.quantity);
                                       
                        return (
                            <div key={idx} className={`flex items-start gap-2 text-sm ${isPicked ? 'text-gray-400' : 'text-gray-700'}`}>
                                <Check 
                                    size={14} 
                                    className={`${isPicked ? 'text-emerald-500' : 'text-gray-300'} mt-0.5 shrink-0 transition-colors shadow-sm rounded-full ${isPicked ? 'bg-emerald-50' : ''}`} 
                                    strokeWidth={isPicked ? 4 : 2} 
                                />
                                <span className={`${isPicked ? 'font-bold' : 'font-medium'}`}>
                                    {item.name || item.productName || item.product || 'Unknown Product'} 
                                    <span className="text-gray-400 mx-1">×</span> 
                                    {item.quantity} 
                                    <span className="text-[10px] text-gray-500 uppercase ml-1">{item.unit || 'pcs'}</span>
                                    {isPicked && <span className="ml-2 text-[10px] font-black text-emerald-600 uppercase tracking-tighter bg-emerald-50 px-1 rounded italic">Picked</span>}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Actions */}
            {transitions.length > 0 && (
                <div className="flex items-center gap-2 mt-auto">
                    {transitions.reduce((uniq, t) => {
                        const raw = (t.transitionName || t.label || t.action || '').toLowerCase();
                        // Hide "Cancel Order" for warehouse picking view to keep it clean, 
                        // unless it's explicitly requested (user asked for Confirm, Revise, Reject)
                        if (raw.includes('cancel')) return uniq;

                        if (!uniq. some(ext => (ext.transitionName || ext.label || '').toLowerCase() === raw)) {
                            uniq.push(t);
                        }
                        return uniq;
                    }, []).map(t => {
                        const rawName = (t.transitionName || t.label || t.action || '').toLowerCase();
                        const isRevise = rawName.includes('revise');
                        const isReject = rawName.includes('reject') || rawName.includes('return');
                        const isConfirm = rawName.includes('ready') || rawName.includes('approve') || rawName.includes('confirm');
                        
                        let style = 'bg-blue-600 text-white hover:bg-blue-700 shadow-md shadow-blue-200/50';
                        if (isRevise || isReject) {
                            style = 'bg-rose-500 text-white hover:bg-rose-600 shadow-md shadow-rose-200/50';
                        }
                        
                        let cleanLabel = t.transitionName || t.label || t.action || '';
                        if (isConfirm) cleanLabel = 'Confirm';
                        if (isReject) cleanLabel = 'Reject';
                        if (isRevise) cleanLabel = 'Revise';

                        return (
                            <button
                                key={t.publicId || t.id}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (isRevise || isReject) onRevise(order, t, isRevise ? 'REVISE' : 'REJECT');
                                    else onTransition(order, t);
                                }}
                                disabled={loadingTransition}
                                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-bold transition-all active:scale-95 disabled:opacity-50 ${style}`}
                            >
                                {loadingTransition ? <RefreshCw size={14} className="animate-spin" /> : getTransitionIcon(rawName)}
                                {cleanLabel}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const XIcon = ({ size, className, strokeWidth }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth || 2} strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
);
const EditIcon = ({ size, className, strokeWidth }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth || 2} strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg>
);


const WarehouseRecentOrders = ({ initialOrders, onViewOrder, sortOrder = 'asc', standalone = false, onOrdersRefresh }) => {
    const [orders, setOrders] = useState(initialOrders || []);
    const [viewMode, setViewMode] = useState('salesperson'); // 'salesperson' or 'product'
    const [workflow, setWorkflow] = useState(null);
    const [loadingTransition, setLoadingTransition] = useState(false);
    const [standaloneLoading, setStandaloneLoading] = useState(standalone);
    const [salespersonSearch, setSalespersonSearch] = useState('');
    
    // Infinite Scroll States
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [totalOrdersCount, setTotalOrdersCount] = useState(0);
    
    // Detailed Modals/Drawers
    const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
    const [reviseOrder, setReviseOrder] = useState(null);
    const [reviseTransition, setReviseTransition] = useState(null);
    const [modalMode, setModalMode] = useState('REVISE'); // 'REVISE' or 'REJECT'
    
    // Batch Confirm Dialog
    const [confirmModal, setConfirmModal] = useState({ 
        isOpen: false, 
        prodName: '', 
        productPublicId: '' 
    });

    // When standalone, fetch orders on mount
    useEffect(() => {
        if (standalone) {
            const load = async () => {
                setStandaloneLoading(true);
                try {
                    const data = await searchOrders({
                        statusCodes: ['SENT_TO_WAREHOUSE', 'PENDING_WAREHOUSE', 'IN_WAREHOUSE']
                    }, { page: 0, size: 15, sort: 'createdAt,desc' });
                    setOrders(data.orders || []);
                    setTotalOrdersCount(data.totalElements || 0);
                    setHasMore(data.orders?.length < (data.totalElements || 0));
                    setPage(0);
                } catch (e) {
                    console.error('Failed to load orders:', e);
                } finally {
                    setStandaloneLoading(false);
                }
            };
            load();
        }
    }, [standalone]);

    const loadMore = async () => {
        if (loadingMore || !hasMore || !standalone) return;
        setLoadingMore(true);
        try {
            const nextPage = page + 1;
            const data = await searchOrders({
                statusCodes: ['SENT_TO_WAREHOUSE', 'PENDING_WAREHOUSE', 'IN_WAREHOUSE']
            }, { page: nextPage, size: 15, sort: 'createdAt,desc' });
            
            const newOrders = data.orders || [];
            if (newOrders.length > 0) {
                setOrders(prev => {
                    const existingIds = new Set(prev.map(o => o.id));
                    const filteredNew = newOrders.filter(o => !existingIds.has(o.id));
                    const combined = [...prev, ...filteredNew];
                    setHasMore(combined.length < (data.totalElements || 0));
                    return combined;
                });
                setPage(nextPage);
            } else {
                setHasMore(false);
            }
        } catch (e) {
            console.error('Failed to load more orders:', e);
        } finally {
            setLoadingMore(false);
        }
    };

    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - scrollTop - clientHeight < 100) {
            loadMore();
        }
    };

    // Filter to base pending warehouse orders
    const basePendingOrders = useMemo(() => {
        return orders.filter(o => ['SENT_TO_WAREHOUSE', 'PENDING_WAREHOUSE', 'IN_WAREHOUSE'].includes(o.status?.toUpperCase()));
    }, [orders]);

    // Filter by search term and sort
    const pendingOrders = useMemo(() => {
        const term = salespersonSearch.trim().toLowerCase();
        
        const searchFiltered = basePendingOrders.filter(o => {
            if (!term) return true;
            const smName = (o.salesman || '').toLowerCase();
            const smNameAssigned = (o.assignedSalesPersonName || '').toLowerCase();
            const smNameDelegated = (o.delegatedSalesPersonName || '').toLowerCase();
            const smNameObj = (o.assignedSalesPerson?.name || '').toLowerCase();
            const orderRef = (o.orderRef || o.publicId || o.id || '').toLowerCase();
            const custName = (o.customer?.name || o.customerName || '').toLowerCase();
            const custPhone = (o.customer?.phone || '').toLowerCase();
            const vehicle = (o.assignedSalesPersonVehicleNumber || o.delegatedSalesPersonVehicleNumber || '').toLowerCase();
            const matchesItems = (o.items || []).some(item =>
                (item.name || item.productName || item.product || '').toLowerCase().includes(term) ||
                (item.sku || '').toLowerCase().includes(term)
            );

            return smName.includes(term) ||
                smNameAssigned.includes(term) ||
                smNameDelegated.includes(term) ||
                smNameObj.includes(term) ||
                orderRef.includes(term) ||
                custName.includes(term) ||
                custPhone.includes(term) ||
                vehicle.includes(term) ||
                matchesItems;
        });

        return [...searchFiltered].sort((a, b) => {
            const dateA = new Date(a.updatedAt || a.createdAt || 0);
            const dateB = new Date(b.updatedAt || b.createdAt || 0);
            return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
        });
    }, [basePendingOrders, sortOrder, salespersonSearch]);

    // 1. Salesperson-wise logic (Order Cards grouped by Salesperson)
    const groupedBySalesperson = useMemo(() => {
        if (viewMode !== 'salesperson') return null;
        const groups = {};
        pendingOrders.forEach(order => {
            const smName = order.salesman || 'Unassigned';
            if (!groups[smName]) groups[smName] = [];
            groups[smName].push(order);
        });
        return groups;
    }, [pendingOrders, viewMode]);

    // 1.5. Salesperson Items logic (Row wise view of items per salesperson)
    const groupedBySalespersonItems = useMemo(() => {
        if (viewMode !== 'salesperson-items') return null;
        const groups = {};
        pendingOrders.forEach(order => {
            const smName = order.salesman || 'Unassigned';
            const smPublicId = order.assignedSalesPersonPublicId || order.assignedSalesPerson?.publicId || null;
            
            if (!groups[smName]) {
                groups[smName] = { 
                    name: smName, 
                    publicId: smPublicId,
                    vehicleNumber: order.assignedSalesPersonVehicleNumber || order.delegatedSalesPersonVehicleNumber || null,
                    items: {} 
                };
            } else if (!groups[smName].vehicleNumber) {
                groups[smName].vehicleNumber = order.assignedSalesPersonVehicleNumber || order.delegatedSalesPersonVehicleNumber || null;
            }
            
            (order.items || []).forEach(item => {
                const prodName = item.name || item.productName || item.product || 'Unknown Product';
                const unit = item.unit || 'pcs';
                const key = `${prodName}_${unit}`;
                
                if (!groups[smName].items[key]) {
                    groups[smName].items[key] = {
                        productName: prodName,
                        unit: unit,
                        quantity: 0,
                        orders: []
                    };
                }
                groups[smName].items[key].quantity += Number(item.quantity || 0);
                
                const ref = order.orderRef || `#${order.id?.slice(-8)}`;
                const custName = order.customer?.name || 'Unknown Customer';
                
                if (!groups[smName].items[key].orders.find(o => o.ref === ref)) {
                    groups[smName].items[key].orders.push({ ref, custName });
                }
            });
        });
        return groups;
    }, [pendingOrders, viewMode]);

    // 2. Product-wise logic (Picking List aggregated across all pending orders)
    const groupedByProduct = useMemo(() => {
        if (viewMode !== 'product') return null;
        const groups = {};
        
        pendingOrders.forEach(order => {
            const smName = order.salesman || 'Unassigned';
            (order.items || []).forEach(item => {
                const prodName = item.name || item.productName || item.product || 'Unknown Product';
                const productPublicId = item.product?.publicId || item.productPublicId;
                
                if (!groups[prodName]) {
                    groups[prodName] = { 
                        productPublicId,
                        totalQuantity: 0, 
                        unit: item.unit || 'pcs',
                        salespersons: {},
                        alreadyPicked: 0 // Track how many are already marked available
                    };
                }
                groups[prodName].totalQuantity += item.quantity;
                
                const isPicked = item.warehouseQuantity !== null && 
                               item.warehouseQuantity !== undefined && 
                               Number(item.warehouseQuantity) >= Number(item.quantity);
                
                if (isPicked) {
                    groups[prodName].alreadyPicked += item.quantity;
                }
                
                if (!groups[prodName].salespersons[smName]) {
                    groups[prodName].salespersons[smName] = 0;
                }
                groups[prodName].salespersons[smName] += item.quantity;
            });
        });
        return groups;
    }, [pendingOrders, viewMode]);

    useEffect(() => {
        const loadWorkflow = async () => {
            try {
                let plan = 'BASIC';
                try {
                    const tenant = await getCurrentTenant();
                    plan = tenant.subscriptionPlan || 'BASIC';
                } catch (e) {}
                const wf = await getWorkflowByPlan(plan);
                setWorkflow(wf);
            } catch (err) {
                console.error("Failed to load workflow", err);
            }
        };
        loadWorkflow();
    }, []);

    useEffect(() => {
        setOrders(initialOrders || []);
    }, [initialOrders]);

    const getAvailableTransitions = useCallback((order) => {
        if (!workflow || !order || !order.status) return [];
        return workflow.transitions.filter(t => {
            if (t.fromStatus.statusCode !== order.status) return false;
            if (t.allowedRoles.some(r => r === 'WAREHOUSE_PERSON' || r === 'ROLE_WAREHOUSE_PERSON')) return true;
            if (t.allowedRoles.some(r => ['SUPPLIER', 'SYSTEM_ADMIN', 'TENANT_OWNER'].includes(r.replace('ROLE_', '')))) return true;
            return false;
        });
    }, [workflow]);

    const refreshOrders = async () => {
        try {
            let freshOrders = [];
            if (standalone) {
                const data = await searchOrders({
                    statusCodes: ['SENT_TO_WAREHOUSE', 'PENDING_WAREHOUSE', 'IN_WAREHOUSE']
                }, { page: 0, size: 1000, sort: 'createdAt,desc' });
                freshOrders = data.orders || [];
            } else {
                // Not standard usage for standalone but fallback
                freshOrders = initialOrders || [];
            }
            setOrders(freshOrders);
            onOrdersRefresh?.(freshOrders);
        } catch (e) {
            console.error("Failed to refresh orders", e);
        }
    };
    const handleTransition = async (order, transition) => {
        const rawName = (transition.transitionName || transition.label || transition.action || '').toLowerCase();
        
        // If it's a reject action, we want to show the modal first to capture reason
        if (rawName.includes('reject') || rawName.includes('return')) {
            setModalMode('REJECT');
            setReviseOrder(order);
            setReviseTransition(transition);
            return;
        }

        setLoadingTransition(true);
        try {
            await executeTransition(order.id, transition.publicId || transition.id, {});
            await refreshOrders();
        } catch (err) {
            alert(`Action failed: ${err?.response?.data?.message || err.message}`);
        } finally {
            setLoadingTransition(false);
        }
    };

    const handleReviseTrigger = (order, transition, mode = 'REVISE') => {
        setModalMode(mode);
        setReviseOrder(order);
        setReviseTransition(transition);
    };

    const handleBatchProductConfirm = async (prodName, productPublicId) => {
        if (!productPublicId) {
            console.error("Product Public ID missing for", prodName);
            alert("Unexpected error: Product ID not found. Please refresh the page.");
            return;
        }
        setConfirmModal({ isOpen: true, prodName, productPublicId });
    };

    const submitBatchConfirm = async () => {
        const { productPublicId } = confirmModal;
        setLoadingTransition(true);
        try {
            await batchConfirmProduct(productPublicId, "Batch availability marked from picking view");
            setConfirmModal({ isOpen: false, prodName: '', productPublicId: '' });
            await refreshOrders();
        } catch (err) {
            alert(`Batch action failed: ${err?.response?.data?.message || err.message}`);
        } finally {
            setLoadingTransition(false);
        }
    };

    const handleModalSubmit = async (order, transition, payload) => {
        setLoadingTransition(true);
        try {
            await executeTransition(order.id, transition.publicId || transition.id, payload);
            setReviseOrder(null);
            setReviseTransition(null);
            await refreshOrders();
        } catch (err) {
            alert(`${modalMode === 'REVISE' ? 'Revision' : 'Rejection'} failed: ${err?.response?.data?.message || err.message}`);
        } finally {
            setLoadingTransition(false);
        }
    };

    return (
        <div className={`bg-white ${standalone ? 'h-full' : 'rounded-[20px] shadow-sm ring-1 ring-gray-100'} flex-1 flex flex-col overflow-hidden relative`}>
            {/* Header & Toggle */}
            <div className={`${standalone ? 'px-6 pt-6 pb-4' : 'p-4 sm:p-6'} border-b border-gray-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-gray-50/50`}>
                <div>
                    <h2 className={`${standalone ? 'text-2xl' : 'text-lg'} font-black text-gray-900 tracking-tight flex items-center gap-2 flex-wrap`}>
                        <span>{standalone ? 'New Orders' : 'Pending Orders'}</span>
                        {(standalone ? totalOrdersCount : orders.length) > 0 && (
                            <span className="text-xs font-bold bg-blue-100 text-blue-700 px-2.5 py-0.5 rounded-full whitespace-nowrap">
                                {standalone ? totalOrdersCount : orders.length} Total
                            </span>
                        )}
                    </h2>
                    <p className="text-xs text-gray-500 font-medium mt-0.5">
                        {standalone ? 'Latest pending orders — newest first' : 'Orders waiting for warehouse processing'}
                    </p>
                </div>
                
                <div className="flex bg-gray-100/80 p-1 rounded-xl w-full sm:w-auto overflow-x-auto hidden-scrollbar ring-1 ring-gray-200/50">
                    <button
                        onClick={() => setViewMode('salesperson')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all whitespace-nowrap min-w-max flex-1 sm:flex-initial justify-center ${viewMode === 'salesperson'
                            ? 'bg-white text-blue-600 shadow-sm font-bold ring-1 ring-black/5'
                            : 'text-gray-600 hover:text-gray-900 font-medium hover:bg-gray-200/50'
                            }`}
                    >
                        <Users size={16} />
                        Salesperson Orders
                    </button>
                    <button
                        onClick={() => setViewMode('salesperson-items')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all whitespace-nowrap min-w-max flex-1 sm:flex-initial justify-center ${viewMode === 'salesperson-items'
                            ? 'bg-white text-blue-600 shadow-sm font-bold ring-1 ring-black/5'
                            : 'text-gray-600 hover:text-gray-900 font-medium hover:bg-gray-200/50'
                            }`}
                    >
                        <List size={16} />
                        Salesperson Items
                    </button>
                    <button
                        onClick={() => setViewMode('product')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all whitespace-nowrap min-w-max flex-1 sm:flex-initial justify-center ${viewMode === 'product'
                            ? 'bg-white text-blue-600 shadow-sm font-bold ring-1 ring-black/5'
                            : 'text-gray-600 hover:text-gray-900 font-medium hover:bg-gray-200/50'
                            }`}
                    >
                        <Package size={16} />
                        Product-wise (Picking)
                    </button>
                </div>
            </div>

            {/* Search Filter Bar & Actions */}
            {(basePendingOrders.length > 0 || salespersonSearch) && (
                <div className="px-6 py-3 bg-white border-b border-gray-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div className="relative group w-full sm:max-w-md">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Search size={16} className="text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                        </div>
                        <input
                            type="text"
                            placeholder="Search by salesperson, customer, order ID, product..."
                            value={salespersonSearch}
                            onChange={(e) => setSalespersonSearch(e.target.value)}
                            className="block w-full pl-10 pr-3 py-2 border border-gray-200 rounded-xl text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-gray-50/50 hover:bg-gray-50 transition-all"
                        />
                        {salespersonSearch && (
                            <button 
                                onClick={() => setSalespersonSearch('')}
                                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                            >
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        )}
                    </div>
                    
                    {viewMode === 'salesperson-items' && (
                        <div className="flex items-center gap-2 w-full sm:w-auto overflow-x-auto pb-1 sm:pb-0 hide-scrollbar">
                            <button
                                onClick={async () => {
                                    setLoadingTransition(true);
                                    try {
                                        await downloadPickingListPdf(null, null, true); // true = groupBySalesperson
                                    } catch (err) {
                                        alert("Failed to download PDF: " + err.message);
                                    } finally {
                                        setLoadingTransition(false);
                                    }
                                }}
                                disabled={loadingTransition}
                                className="flex items-center justify-center gap-2 px-4 py-2 bg-violet-50 text-violet-700 hover:bg-violet-100 border border-violet-200 rounded-xl text-sm font-bold transition-all shadow-sm active:scale-95 disabled:opacity-50 shrink-0 whitespace-nowrap"
                            >
                                <Printer size={16} className="text-violet-500" />
                                Print by Salesperson
                            </button>
                            <button
                                onClick={async () => {
                                    setLoadingTransition(true);
                                    try {
                                        await downloadPickingListPdf(null, null, false); // false = Consolidated
                                    } catch (err) {
                                        alert("Failed to download PDF: " + err.message);
                                    } finally {
                                        setLoadingTransition(false);
                                    }
                                }}
                                disabled={loadingTransition}
                                className="flex items-center justify-center gap-2 px-4 py-2 bg-white text-gray-700 hover:bg-gray-50 border border-gray-200 rounded-xl text-sm font-bold transition-all shadow-sm active:scale-95 disabled:opacity-50 shrink-0 whitespace-nowrap"
                            >
                                <List size={16} className="text-gray-400" />
                                Print Master List
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* Content Body */}
            <div 
                onScroll={handleScroll}
                className="flex-1 overflow-y-auto p-4 sm:p-6 bg-gray-50/30"
            >
                {pendingOrders.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-gray-500 gap-3">
                        {salespersonSearch ? (
                            <>
                                <Search size={36} className="text-gray-400 opacity-60" />
                                <span className="font-bold text-base text-gray-700">No orders matching "{salespersonSearch}"</span>
                                <p className="text-xs text-gray-400 max-w-md text-center">Try searching with a different salesperson name, customer, order ID, product, or vehicle number.</p>
                                <button
                                    onClick={() => setSalespersonSearch('')}
                                    className="mt-2 px-4 py-2 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-xl text-xs font-bold transition-all shadow-sm"
                                >
                                    Clear Search
                                </button>
                            </>
                        ) : (
                            <>
                                <Check size={32} strokeWidth={3} className="text-emerald-400 opacity-50" />
                                <span className="font-bold text-sm">All caught up! No pending orders.</span>
                            </>
                        )}
                    </div>
                ) : (
                    viewMode === 'salesperson' ? (
                        /* Salesperson-wise View */
                        <div className="space-y-6">
                            {Object.entries(groupedBySalesperson || {}).map(([salesman, smOrders]) => {
                                const firstOrder = smOrders.find(o => o.assignedSalesPersonPublicId || o.delegatedSalesPersonPublicId) || smOrders[0];
                                const smPublicId = firstOrder?.assignedSalesPersonPublicId || firstOrder?.delegatedSalesPersonPublicId;
                                const smVehicleNo = firstOrder?.assignedSalesPersonVehicleNumber || firstOrder?.delegatedSalesPersonVehicleNumber;

                                return (
                                    <div key={salesman} className="bg-white rounded-xl p-5 ring-1 ring-gray-200 shadow-sm">
                                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4 pb-4 border-b border-gray-100">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center border border-blue-100">
                                                    <Users size={16} strokeWidth={2.5} />
                                                </div>
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="font-black text-gray-800 tracking-tight text-lg">{salesman}</span>
                                                    {smVehicleNo && (
                                                        <span className="inline-flex items-center gap-1 text-[11px] bg-violet-50 text-violet-700 px-2 py-0.5 rounded border border-violet-100 font-bold uppercase tracking-tight">
                                                            <Truck size={12} className="text-violet-500" />
                                                            {smVehicleNo}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2 self-end sm:self-auto">
                                                <span className="text-xs font-bold bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                                                    {smOrders.length} Orders
                                                </span>
                                                <button
                                                    onClick={async () => {
                                                        setLoadingTransition(true);
                                                        try {
                                                            await downloadPickingListPdf(smPublicId, salesman);
                                                        } catch (err) {
                                                            alert("Failed to download PDF: " + err.message);
                                                        } finally {
                                                            setLoadingTransition(false);
                                                        }
                                                    }}
                                                    disabled={loadingTransition}
                                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg text-xs font-bold transition-all shadow-sm active:scale-95 disabled:opacity-50"
                                                >
                                                    <Printer size={14} className="text-gray-500" />
                                                    Print Report
                                                </button>
                                            </div>
                                        </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {smOrders.map(order => (
                                            <SimpleWarehouseOrderCard
                                                key={order.id}
                                                order={order}
                                                transitions={getAvailableTransitions(order)}
                                                onTransition={handleTransition}
                                                onRevise={handleReviseTrigger}
                                                loadingTransition={loadingTransition}
                                                onViewDetail={setSelectedOrderDetails}
                                            />
                                        ))}
                                    </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : viewMode === 'salesperson-items' ? (
                        /* Salesperson Items View (Row-wise picking list per salesperson) */
                        <div className="space-y-6">
                            {Object.values(groupedBySalespersonItems || {}).map(group => (
                                <div key={group.name} className="bg-white rounded-xl shadow-sm ring-1 ring-gray-200 overflow-hidden">
                                    <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
                                        <h3 className="text-sm font-black text-gray-800 flex items-center gap-2 flex-wrap">
                                            <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center border border-blue-100">
                                                <List size={16} strokeWidth={2.5} />
                                            </div>
                                            <span className="tracking-tight text-lg">{group.name}</span>
                                            {group.vehicleNumber && (
                                                <span className="inline-flex items-center gap-1 text-[11px] bg-violet-50 text-violet-700 px-2 py-0.5 rounded border border-violet-100 font-bold uppercase tracking-tight">
                                                    <Truck size={12} className="text-violet-500" />
                                                    {group.vehicleNumber}
                                                </span>
                                            )}
                                            <span className="text-xs font-bold bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full ml-2">
                                                {Object.keys(group.items).length} Unique Items
                                            </span>
                                        </h3>
                                        <button
                                            onClick={async () => {
                                                setLoadingTransition(true);
                                                try {
                                                    await downloadPickingListPdf(group.publicId, group.name);
                                                } catch (err) {
                                                    alert("Failed to download PDF: " + err.message);
                                                } finally {
                                                    setLoadingTransition(false);
                                                }
                                            }}
                                            disabled={loadingTransition}
                                            className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg text-xs font-bold transition-all shadow-sm active:scale-95 disabled:opacity-50"
                                        >
                                            <Printer size={14} className="text-gray-500" />
                                            Print Report
                                        </button>
                                    </div>
                                    <div className="overflow-x-auto w-full">
                                        <table className="w-full text-left min-w-[600px]">
                                            <thead className="bg-blue-50/80 text-blue-800 border-y border-blue-100 font-bold">
                                                <tr>
                                                    <th className="px-5 py-3 text-xs font-bold text-gray-500 uppercase tracking-widest">Product Name</th>
                                                    <th className="px-5 py-3 text-xs font-bold text-gray-500 uppercase tracking-widest text-center">Qty Required</th>
                                                    <th className="px-5 py-3 text-xs font-bold text-gray-500 uppercase tracking-widest text-right">Related Orders</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-50">
                                                {Object.values(group.items).sort((a, b) => a.productName.localeCompare(b.productName)).map(item => (
                                                    <tr key={`${item.productName}_${item.unit}`} className="hover:bg-gray-50/50 transition-colors">
                                                        <td className="px-5 py-4">
                                                            <span className="font-bold text-gray-900">{item.productName}</span>
                                                        </td>
                                                        <td className="px-5 py-4 text-center">
                                                            <span className="inline-flex items-center justify-center min-w-[3rem] px-2 py-1 rounded-lg bg-blue-50 text-blue-800 font-black text-sm border border-blue-100">
                                                                {item.quantity} <span className="text-[10px] ml-1 uppercase">{item.unit}</span>
                                                            </span>
                                                        </td>
                                                        <td className="px-5 py-4 text-right">
                                                            <div className="flex flex-wrap justify-end gap-1.5 max-w-[280px] ml-auto">
                                                                {item.orders.map(o => (
                                                                    <div key={o.ref} title={`${o.custName} (${o.ref})`} className="flex items-center gap-1.5 text-[10px] bg-gray-50 text-gray-700 pl-2 pr-1 py-0.5 rounded-md border border-gray-200 shadow-sm max-w-[140px]">
                                                                        <span className="font-bold truncate">{o.custName}</span>
                                                                        <span className="text-[9px] bg-white text-gray-500 px-1 py-0.5 rounded font-mono border border-gray-100 shrink-0">
                                                                            {o.ref}
                                                                        </span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        /* Product-wise View (Picking List) */
                        <div className="bg-white rounded-xl shadow-sm ring-1 ring-gray-200 overflow-hidden">
                            <div className="overflow-x-auto w-full">
                                <table className="w-full text-left min-w-[750px]">
                                    <thead className="bg-blue-50/80 text-blue-800 border-y border-blue-100 font-bold">
                                        <tr>
                                            <th className="px-5 py-3 text-xs font-bold text-gray-500 uppercase tracking-widest">Product Name</th>
                                            <th className="px-5 py-3 text-xs font-bold text-gray-500 uppercase tracking-widest">Salesperson Breakdown</th>
                                            <th className="px-5 py-3 text-xs font-bold text-gray-500 uppercase tracking-widest text-right">Total Pick Qty</th>
                                            <th className="px-5 py-3 text-xs font-bold text-gray-500 uppercase tracking-widest text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-50">
                                        {Object.entries(groupedByProduct || {}).map(([prodName, data]) => {
                                            const isAllPicked = data.alreadyPicked >= data.totalQuantity;
                                            
                                            return (
                                                <tr key={prodName} className="hover:bg-gray-50/50 transition-colors">
                                                    <td className="px-5 py-4">
                                                        <div className="flex items-center gap-3">
                                                            <div className={`w-8 h-8 rounded-lg ${isAllPicked ? 'bg-emerald-50 text-emerald-600' : 'bg-violet-50 text-violet-600'} flex items-center justify-center transition-colors`}>
                                                                {isAllPicked ? <Check size={14} strokeWidth={3} /> : <Package size={14} />}
                                                            </div>
                                                            <span className={`font-bold ${isAllPicked ? 'text-gray-400 line-through' : 'text-gray-900'}`}>{prodName}</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-5 py-4">
                                                        <div className="flex flex-wrap gap-2">
                                                            {Object.entries(data.salespersons).map(([sm, qty]) => (
                                                                <span key={sm} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-gray-100 text-gray-700 text-xs font-bold">
                                                                    <Users size={12} className="text-gray-400" />
                                                                    {sm}: <span className="text-violet-600">{qty}</span>
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </td>
                                                    <td className="px-5 py-4 text-right">
                                                        <div className="flex flex-col items-end">
                                                            <span className={`inline-flex items-center justify-center min-w-[3rem] px-2 py-1 rounded-lg ${isAllPicked ? 'bg-emerald-100 text-emerald-800' : 'bg-violet-100 text-violet-800'} font-black text-sm`}>
                                                                {data.totalQuantity} <span className="text-[10px] ml-1 uppercase">{data.unit}</span>
                                                            </span>
                                                            {data.alreadyPicked > 0 && !isAllPicked && (
                                                                <span className="text-[9px] font-black text-emerald-600 uppercase mt-1">
                                                                    Picked: {data.alreadyPicked}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="px-5 py-4 text-center">
                                                        <button
                                                            onClick={() => handleBatchProductConfirm(prodName, data.productPublicId)}
                                                            disabled={loadingTransition || isAllPicked}
                                                            className={`h-10 px-6 rounded-2xl text-[11px] font-black uppercase tracking-wider transition-all active:scale-95 disabled:opacity-50 flex items-center gap-2 mx-auto overflow-hidden relative group ${
                                                                isAllPicked 
                                                                ? 'bg-emerald-50 text-emerald-600 border-2 border-emerald-100 shadow-none' 
                                                                : 'bg-white text-blue-600 border-2 border-blue-100 hover:border-blue-600 hover:bg-blue-50 shadow-sm'
                                                            }`}
                                                        >
                                                            {loadingTransition ? (
                                                                <RefreshCw size={14} className="animate-spin" />
                                                            ) : isAllPicked ? (
                                                                <Check size={14} strokeWidth={4} className="animate-in zoom-in-50 duration-300" />
                                                            ) : (
                                                                <div className="w-4 h-4 rounded-full border-2 border-blue-200 group-hover:border-blue-600 group-hover:bg-blue-600 flex items-center justify-center transition-all">
                                                                    <Check size={10} className="text-white opacity-0 group-hover:opacity-100 scale-50 group-hover:scale-100 transition-all font-black" strokeWidth={5} />
                                                                </div>
                                                            )}
                                                            <span>{isAllPicked ? 'Picked' : 'Mark Available'}</span>
                                                            
                                                            {/* Animated background glow for active state */}
                                                            {!isAllPicked && !loadingTransition && (
                                                                <div className="absolute inset-0 bg-blue-600/0 group-active:bg-blue-600/10 transition-colors pointer-events-none" />
                                                            )}
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )
                )}
            </div>

            {/* Support Modals & Drawers */}
            {reviseOrder && (
                <WarehouseRejectModal
                    order={reviseOrder}
                    transition={reviseTransition}
                    mode={modalMode}
                    loading={loadingTransition}
                    onClose={() => {
                        setReviseOrder(null);
                        setReviseTransition(null);
                    }}
                    onSubmit={handleModalSubmit}
                />
            )}
            {selectedOrderDetails && (
                <OrderDetailDrawer
                    order={selectedOrderDetails}
                    transitions={getAvailableTransitions(selectedOrderDetails)}
                    onTransition={handleTransition}
                    loadingTransition={loadingTransition}
                    onClose={() => setSelectedOrderDetails(null)}
                    role={'WAREHOUSE_PERSON'}
                />
            )}

            {/* Reusable Confirmation Modal */}
            <ConfirmDialog
                isOpen={confirmModal.isOpen}
                onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
                onConfirm={submitBatchConfirm}
                title="Confirm Availability"
                message={`Are you sure that all requested "${confirmModal.prodName}" are available in stock? This will update all pending orders.`}
                confirmText="Yes, Marks Available"
                loading={loadingTransition}
                type="info"
            />
        </div>
    );
};

export default WarehouseRecentOrders;
